import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
  createClass as createClassService,
  updateClass as updateClassService,
  deleteClass as deleteClassService,
  archiveClass as archiveClassService,
  getClassesSkipLimit as getClassesSkipLimitService,
  batchUpdateClasses as batchUpdateClassesService,
  getClassById
} from '../../services/classes/classes.service.js'
import { validate } from '../../utils/index.js'
import { handlerErrorV2 } from '../../utils/errors.jsx'
import {
  updateClassForm,
  updateClassesList,
  updateClassesListMethod
} from '../../store/actions/class.actions.js'
import isEmpty from 'lodash/isEmpty'
import { toggleAlertInfo, toggleAlertModule } from '../../store/actions/alert.actions'
import { useParams } from 'react-router-dom'

const useClasses = () => {
  const subjects = useSelector((state) => state?.subject?.list || [])
  const { studentId } = useParams()
  const existLinceseAssessment = useSelector((state) =>
    state?.tenant?.license?.find((license) => license.code === 'assessment')
  )
  const { enqueueSnackbar } = useSnackbar()
  const dispatch = useDispatch()
  const [isLoadingList, setIsLoadingList] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const getClasses = useCallback(async (curriculumId, skip, rowsPerPage, filter) => {
    if (curriculumId) {
      setIsLoadingList(true)
      try {
        const filterData = {
          code: filter?.code,
          name: filter?.name,
          yearLevel: filter?.yearLevel,
          curriculumElements: filter?.curriculumElements,
          subjectId: filter?.subjectId,
          teachers: filter?.teachersId,
          studentId,
          ids: filter?.ids
        }
        const response = await getClassesSkipLimitService(curriculumId, skip, rowsPerPage, filterData)
        if (!response?.data && response?.error) {
          enqueueSnackbar(response.error.message || 'An error ocurred', {
            variant: 'error'
          })
        } else {
          dispatch(
            updateClassesList(curriculumId, {
              data: response?.data?.data || [],
              count: response?.data.count?.length,
              totalIds: response?.data?.count.map(({ _id, code }) => ({ _id, code })) || [],
              teachers: response?.data?.teachers || [],
              students: response?.data?.students || []
            })
          )
        }
      } catch (error) {
        const message = handlerErrorV2(error)
        enqueueSnackbar(message, { variant: 'error' })
      } finally {
        setIsLoadingList(false)
      }
    }
  }, [])

  const validateForm = (form, type = 'create') => {
    let fieldRules = {}

    if (type === 'create-0') {
      fieldRules = {
        code: 'required',
        yearLevel: 'required'
      }
      if (existLinceseAssessment) {
        fieldRules.curriculumId = 'required|validObjectId'

        if (subjects.length > 0) {
          fieldRules.subjectId = 'required'
        }
      }
    }
    if (type === 'create-1') {
      if (isEmpty(form.teachers)) {
        return { error: true, message: 'Teachers is required' }
      }
    }
    if (type === 'create-2') {
      if (isEmpty(form.students)) {
        return { error: true, message: 'Students is required' }
      }
    }

    if (type === 'create') {
      fieldRules = {
        code: 'required',
        yearLevel: 'required'
      }
    }
    if (type === 'basic-information') {
      fieldRules = {
        code: 'required',
        yearLevel: 'required'
      }
    }
    if (type === 'subject') {
      if (subjects.length > 0) {
        fieldRules.subjectId = 'required'
      }
    }

    if (type === 'curriculum-elements') {
      fieldRules = {
        curriculumId: 'required',
        curriculumElements: 'required'
      }
    }

    if (type === 'add-students' || type === 'remove-students') {
      if (isEmpty(form.students)) {
        return { error: true, message: 'Students is required' }
      }
    }

    if (type === 'add-teachers' || type === 'remove-teachers') {
      if (isEmpty(form.teachers)) {
        return { error: true, message: 'Teachers is required' }
      }
    }

    const errors = validate(fieldRules, {
      ...form,
      ...(form.curriculumElements || {})
    })
    if (!isEmpty(errors)) {
      return { error: true, ...errors }
    }

    return { error: false }
  }

  const formData = (form, type = 'create') => {
    if (type === 'basic-information') {
      return {
        code: form.code,
        name: form.name,
        yearLevel: form.yearLevel,
        description: form.description
      }
    }

    if (type === 'curriculum-elements') {
      return {
        curriculumElements: form.curriculumElements,
        curriculumId: form.curriculumId
      }
    }

    if (type === 'subject') {
      return {
        subjectId: form.subjectId
      }
    }

    if (type === 'add-students' || type === 'remove-students') {
      return {
        students: form.students
      }
    }

    if (type === 'add-teachers' || type === 'remove-teachers') {
      return {
        teachers: form.teachers
      }
    }
    if (type === 'year-level') {
      return {
        yearLevel: form.yearLevel
      }
    }

    return {
      code: form.code,
      name: form.name,
      yearLevel: form.yearLevel,
      description: form.description,

      subjectId: form.subjectId,

      curriculumId: form.curriculumId,
      curriculumElements: form.curriculumElements,

      students: form.students,
      teachers: form.teachers,

      unitOfLearnings: form.unitOfLearnings?.map((item) => (item._id ? item._id : item))
    }
  }

  const createClass = async (curriculumId, form = {}) => {
    const result = validateForm(form)
    if (result.error) {
      return result
    }

    setIsLoading(true)
    try {
      dispatch(
        toggleAlertInfo(true, 'Creating class', 'Please wait as this may take a few minutes', 'loading')
      )
      const data = formData(form)
      const response = await createClassService(curriculumId, data)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        dispatch(updateClassesListMethod(curriculumId, 'POST', response.data))
        enqueueSnackbar('Class created successfully', {
          variant: 'success'
        })
        dispatch(toggleAlertModule())
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(true)
      dispatch(toggleAlertInfo())
    }
  }

  const updateClass = async (curriculumId, classId, type, form = {}) => {
    if (!['basic-information', 'curriculum-elements', 'subject'].includes(type)) {
      return { error: true, message: 'Update not allowed' }
    }

    const result = validateForm(form, type)
    if (result.error) {
      return result
    }

    setIsLoading(true)
    try {
      const data = formData(form, type)
      const response = await updateClassService(curriculumId, classId, type, data)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        const respCurriculumId = response?.data?.curriculumId
        if (curriculumId === 'unassigned') {
          dispatch(updateClassesListMethod(curriculumId, 'DELETE', classId))
        } else if (respCurriculumId !== curriculumId) {
          dispatch(updateClassesListMethod(curriculumId, 'DELETE', classId))
          dispatch(updateClassesListMethod(respCurriculumId, 'POST', response.data))
        } else {
          delete response?.data?.students
          delete response?.data?.teachers
          dispatch(updateClassesListMethod(curriculumId, 'PUT', response.data))
        }
        dispatch(updateClassForm(response.data))
        enqueueSnackbar('Class updated successfully', {
          variant: 'success'
        })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }
  const addUsersToClass = async (curriculumId, classId, type, form = {}) => {
    if (!['add-students', 'add-teachers'].includes(type)) {
      return { error: true, message: 'Update not allowed' }
    }

    const result = validateForm(form, type)
    if (result.error) {
      return result
    }
    const userType = type.replace('add-', '')

    dispatch(
      toggleAlertInfo(true, `Adding ${userType}`, 'Please wait as this may take a few minutes', 'loading')
    )
    try {
      const data = formData(form, type)
      if (classId === 'batch') {
        data.classIds = form._id
      }
      const response = await updateClassService(curriculumId, classId, `add/${userType}`, data)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        if (classId === 'batch') {
          enqueueSnackbar('Classes updated successfully', {
            variant: 'success'
          })
        } else {
          const result = await getClassById(curriculumId, classId)
          if (result.data) {
            dispatch(updateClassForm(result.data))
          }
          enqueueSnackbar('Class updated successfully', {
            variant: 'success'
          })
        }
        dispatch(toggleAlertModule())
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(true)
      dispatch(toggleAlertInfo())
    }
  }

  const removeUsersFromClass = async (curriculumId, classId, type, form = {}) => {
    if (!['remove-students', 'remove-teachers'].includes(type)) {
      return { error: true, message: 'Update not allowed' }
    }

    const result = validateForm(form, type)
    if (result.error) {
      return result
    }

    try {
      const data = formData(form, type)
      if (classId === 'batch') {
        data.classIds = form._id
      }
      const response = await updateClassService(
        curriculumId,
        classId,
        `remove/${type.replace('remove-', '')}`,
        data
      )
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        if (classId === 'batch') {
          enqueueSnackbar('Classes updated successfully', {
            variant: 'success'
          })
        } else {
          const result = await getClassById(curriculumId, classId)
          if (result.data) {
            dispatch(updateClassForm(result.data))
          }
          enqueueSnackbar('Class updated successfully', {
            variant: 'success'
          })
        }
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    }
  }

  const batchUpdateClasses = async (curriculumId, classIds, type, form = {}) => {
    if (!['curriculum-elements', 'subject', 'year-level'].includes(type)) {
      return { error: true, message: 'Update not allowed' }
    }

    const result = validateForm(form, type)
    if (result.error) {
      return result
    }

    setIsLoading(true)
    try {
      const data = formData(form, type)
      const response = await batchUpdateClassesService(curriculumId, type, {
        ...data,
        classIds
      })
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        if (curriculumId === 'unassigned') {
          dispatch(updateClassesListMethod(curriculumId, 'BATCH_DELETE', classIds))
        } else {
          dispatch(
            updateClassesListMethod(
              curriculumId,
              'BATCH_PUT',
              response.data.map((row) => {
                delete row.students
                delete row.teachers
                return row
              })
            )
          )
        }
        enqueueSnackbar('Classes updated successfully', {
          variant: 'success'
        })
        dispatch(toggleAlertModule())

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  const deleteClass = async (curriculumId, classId) => {
    setIsLoading(true)
    try {
      const response = await deleteClassService(curriculumId, classId)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        dispatch(updateClassesListMethod(curriculumId, 'DELETE', classId))
        enqueueSnackbar('Class deleted successfully', {
          variant: 'success'
        })

        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  const archiveClass = async (curriculumId, classId) => {
    setIsLoading(true)
    try {
      const response = await archiveClassService(curriculumId, classId)
      if (!response.data && response.error) {
        return {
          error: true,
          message: response.error.message || 'An error ocurred'
        }
      } else {
        dispatch(updateClassesListMethod(curriculumId, 'DELETE', classId))
        enqueueSnackbar('Class archived successfully', {
          variant: 'success'
        })
        return { error: false }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      return { error: true, message }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoadingList,
    isLoading,
    validateFormClasses: validateForm,
    getClasses,
    createClass,
    updateClass,
    batchUpdateClasses,
    addUsersToClass,
    removeUsersFromClass,
    deleteClass,
    archiveClass
  }
}

export default useClasses
